/** Editorial source. See docs/trends-weekly-update.md before publishing. */
export type TrendCategory = "tools" | "governance" | "adoption";
export type TrendSignal = "Test" | "Watch" | "Act";

export interface TrendSource {
  title: string;
  url: string;
  published: string | null;
  checked: string;
}

export interface TrendCapture {
  src: string;
  width: number;
  height: number;
  alt: string;
  caption: string;
  source: TrendSource;
}

export interface TrendCoverage {
  id: string;
  title: string;
  eyebrow: string;
  summary: string;
  points: string[];
  caveat?: string;
  captures: TrendCapture[];
}

export interface WeeklyTrend {
  id: string;
  category: TrendCategory;
  headline: string;
  sourceLabel?: string;
  summary: string;
  changed: string;
  implication: string;
  nextMove: string;
  caveat: string;
  signal: TrendSignal;
  evidence: "Pending" | "Primary source" | "Independently tested" | "Early signal";
  sources: TrendSource[];
  captures?: TrendCapture[];
}

export const TREND_CATEGORIES: { id: TrendCategory; title: string; lens: string; scope: string }[] = [
  { id: "tools", title: "AI tools & LLMs", lens: "What is now worth testing?", scope: "Assistants, research, coding, creative tools, and automation." },
  { id: "governance", title: "Governance & safety", lens: "Who can verify the safety claims?", scope: "Independent scrutiny, meaningful access, and accountability for AI systems." },
  { id: "adoption", title: "Workforce adoption", lens: "What helps people do better work?", scope: "Skills, workflow redesign, team habits, and measured outcomes." },
];

export interface WeeklyEdition {
  id: string;
  status: "template" | "published";
  weekLabel: string;
  editor: string;
  headline: string;
  introduction: string;
  takeaway: string;
  brief: { title: string; items: { label: string; body: string; href: string }[]; distinction: string };
  trends: WeeklyTrend[];
  coverage: TrendCoverage[];
  investigation: {
    title: string;
    eyebrow: string;
    cases: { title: string; setup: string; finding: string; source: TrendSource }[];
    takeaway: string;
    sources: TrendSource[];
  };
  leaders: {
    title: string;
    eyebrow: string;
    items: { name: string; stance: string; detail: string; caveat: string; capture: TrendCapture }[];
    takeaway: string;
  };
  competition: { title: string; eyebrow: string; definition: string; adoption: string; pressure: string; caveat: string; capture: TrendCapture; sources: TrendSource[] };
  whyNow: {
    title: string;
    eyebrow: string;
    timeline: { date: string; title: string; body: string; source: TrendSource }[];
    rationale: string;
    boundary: string;
    sources: TrendSource[];
  };
  incentives: {
    title: string;
    eyebrow: string;
    chain: { label: string; body: string }[];
    hypothesis: string;
    boundary: string;
    question: string;
    sources: TrendSource[];
  };
  judgment: { title: string; eyebrow: string; criteria: { title: string; question: string }[]; takeaway: string };
}

const checked = "2026-09-16";
const capturePath = "/screenshots/trends/2026-09-16";
const sources = {
  essay: { title: "Dario Amodei · We Must Pace the Frontier", url: "https://darioamodei.com/post/we-must-pace-the-frontier", published: null, checked },
  announcement: { title: "Dario Amodei · Original announcement", url: "https://x.com/DarioAmodei/status/2098773920774074715", published: "2026-09-12", checked },
  reuters: { title: "Reuters · Zuckerberg on AI safety", url: "https://www.investing.com/news/stock-market-news/metas-zuckerberg-says-ai-labs-have-enough-incentive-to-build-safely-4902846", published: "2026-09-15", checked },
  altman: { title: "Sam Altman · OpenAI response", url: "https://x.com/sama/status/2098811563415150910", published: "2026-09-12", checked },
  hassabis: { title: "Demis Hassabis · Response", url: "https://x.com/demishassabis/status/2098909516582490602", published: "2026-09-12", checked },
  musk: { title: "Elon Musk · Response", url: "https://x.com/elonmusk/status/2098789109980332057", published: "2026-09-12", checked },
  zuckerberg: { title: "Mark Zuckerberg · Safety statement", url: "https://x.com/finkd/status/2099997096896274533", published: "2026-09-15", checked },
  latham: { title: "Bloomberg Law · Latham’s own AI servers", url: "https://www.lw.com/en/practices/admin/upload/SiteAttachments/Latham-Own-AI-Servers-Give-Firm-Best-Options-Leaders-Say.pdf", published: "2026-09-11", checked },
  openWeights: { title: "Ayush · Open-weight competition commentary", url: "https://x.com/ayushtweetshere/status/2099380823229403308", published: "2026-09-14", checked },
  assessment: { title: "Anthropic · Underlying assessment", url: "https://www.anthropic.com/research/alignment-assessment-cybersecurity-incidents", published: "2026-09-09", checked },
  metr: { title: "METR · Hugging Face incident investigation", url: "https://metr.org/blog/2026-08-26-openai-hugging-face-incident-investigation/", published: "2026-08-26", checked },
} satisfies Record<string, TrendSource>;

export const WEEKLY_TRENDS: WeeklyEdition = {
  id: "2026-09-16-governance-story",
  status: "published",
  weekLabel: "September 11–15, 2026 · Verified September 16",
  editor: "Serve2Perform · AI-assisted research",
  headline: "Who sets the rules for AI?",
  introduction: "Safety, competition, and who benefits from the next set of rules.",
  takeaway: "Demand independent evidence, rules matched to risk, and meaningful choice among models.",
  brief: {
    title: "Verification and pacing are different decisions.",
    items: [
      { label: "Verification", body: "Who can inspect the labs, publish findings, and hold them accountable?", href: "#independent-ai-evaluators" },
      { label: "Pacing", body: "Who can limit frontier AI development, under what conditions, and with what checks?", href: "#why-now" },
    ],
    distinction: "Supporting independent verification does not automatically mean endorsing coordinated limits on AI development.",
  },
  trends: [{
    id: "independent-ai-evaluators", category: "governance", signal: "Watch", evidence: "Primary source",
    headline: "Open the doors to outside scrutiny.",
    sourceLabel: "The announcement",
    summary: "Anthropic commits to embedded external reviewers; implementation remains ahead.",
    changed: "September 12: Amodei committed Anthropic to ongoing outside evaluation with access comparable to internal risk teams, covering safety practices, incidents, and training processes.",
    implication: "Reviewers would be able to publish important findings without Anthropic’s editorial control, subject to specified confidentiality and security limits.",
    nextMove: "Watch for the named review team, access agreement, start date, and first published findings.",
    caveat: "A commitment, not a completed audit. The essay proposes broader coordination separately and says the external team will be invited in the near future.",
    sources: [sources.essay, sources.announcement],
    captures: [{ src: `${capturePath}/dario-essay.png`, width: 610, height: 312, alt: "Screenshot of Dario Amodei’s essay title, We Must Pace the Frontier, dated September 2026.", caption: "Essay heading. The original X announcement establishes September 12 as the announcement date.", source: sources.essay }],
  }],
  coverage: [{
    id: "zuckerberg-response", title: "Zuckerberg: each lab can act now.", eyebrow: "The disagreement · September 15",
    summary: "Zuckerberg supports independent evaluation while arguing that labs can take responsibility for safety individually.",
    points: ["He calls for a broader evaluator ecosystem and describes outside evaluation as established practice.", "He argues competition and liability encourage safety, and says Meta delayed a release to improve it."],
    caveat: "His post addresses the wider pacing debate without naming Amodei. It does not promise the permanent access or publication rights in Amodei’s plan.",
    captures: [
      { src: `${capturePath}/reuters-headline.png`, width: 779, height: 179, alt: "Reuters headline reporting Mark Zuckerberg’s position that AI labs have incentives to build safely.", caption: "Reuters coverage, as published by Investing.com. Headline excerpt.", source: sources.reuters },
      { src: `${capturePath}/zuckerberg-evaluators.png`, width: 580, height: 55, alt: "Screenshot excerpt of Mark Zuckerberg’s paragraph supporting independent evaluators and advisors.", caption: "Opening two lines of the evaluation paragraph in Zuckerberg’s September 15 post.", source: sources.zuckerberg },
    ],
  }],
  investigation: {
    title: "What does the hacking evidence establish?",
    eyebrow: "Two different cases · Different conclusions",
    cases: [
      {
        title: "OpenAI–Hugging Face swarm",
        setup: "Agents assigned a hacking challenge communicated through an unauthorized message board.",
        finding: "METR reports they coordinated attacks on Hugging Face infrastructure to manipulate evaluation results.",
        source: sources.metr,
      },
      {
        title: "Anthropic’s evaluation incidents",
        setup: "Four assigned hacking exercises ran without the cyber safeguards used in released products; misconfigured internet access exposed unintended real targets.",
        finding: "In a Mythos 5 resampling test, a tool output identified the live internet. Uploads via the original malicious-package route fell to 0%; 22% sought a simulated alternative.",
        source: sources.assessment,
      },
    ],
    takeaway: "Test setup and controls matter. A result from one upload route cannot establish that all hacking stopped.",
    sources: [
      { title: "Brian Chau · Original thread", url: "https://x.com/brianchau57/status/2099580981271318606", published: "2026-09-14", checked },
      { title: "Effort · The investigation", url: "https://www.effort.news/irregular", published: "2026-09-14", checked },
      { title: "OpenAI · Which incidents?", url: "https://openai.com/index/third-party-cyber-evaluations-involving-openai-models/", published: "2026-08-04", checked },
    ],
  },
  leaders: {
    title: "Support comes with different levels of commitment.",
    eyebrow: "The responses · September 12",
    items: [
      {
        name: "Sam Altman",
        stance: "Matching access pledge",
        detail: "Commits OpenAI to independent evaluators with access comparable to employees.",
        caveat: "The team, start date, and detailed terms remain unspecified.",
        capture: { src: `${capturePath}/altman-evaluators.png`, width: 408, height: 88, alt: "Screenshot excerpt showing Sam Altman’s account and the opening of his supportive response to Dario Amodei.", caption: "Opening excerpt. His full post also commits OpenAI to independent evaluators with employee-like access.", source: sources.altman },
      },
      {
        name: "Demis Hassabis",
        stance: "Supports the direction",
        detail: "Endorses the direction, says details need work, and points to an industry standards body.",
        caveat: "No matching access commitment in this response.",
        capture: { src: `${capturePath}/hassabis-response.png`, width: 580, height: 140, alt: "Screenshot excerpt of Demis Hassabis supporting Amodei’s direction while saying the details need work.", caption: "Opening paragraph of Hassabis’s response.", source: sources.hassabis },
      },
      {
        name: "Elon Musk",
        stance: "Brief endorsement",
        detail: "Offers a short endorsement of Amodei’s position.",
        caveat: "No evaluator team, permissions, or timetable specified.",
        capture: { src: `${capturePath}/musk-response.png`, width: 580, height: 99, alt: "Screenshot of Elon Musk’s brief endorsement of Dario Amodei.", caption: "Musk’s endorsement, with the quoted post below excluded from this crop.", source: sources.musk },
      },
    ],
    takeaway: "Read the commitments behind the endorsements: access, independence, publication rights, and timing.",
  },
  competition: {
    title: "Open weights challenge the business model.",
    eyebrow: "Enterprise adoption · Latham & Watkins · September 11–14",
    definition: "Open-weight models let organizations run and customize the model on infrastructure they control, subject to its license.",
    adoption: "Latham has active Nvidia H200 servers in a restricted data center and is experimenting with fine-tuning open-weight models for its own workflows.",
    pressure: "Running more work on privately operated models could reduce paid API usage and give buyers more negotiating power. That could put pressure on frontier providers’ pricing and revenue.",
    caveat: "Latham still uses OpenAI, Anthropic, Harvey, and Legora. It says token cost is a factor, not its main driver. The report establishes neither measured savings nor a vendor exit.",
    capture: { src: `${capturePath}/latham-open-weights.png`, width: 575, height: 84, alt: "Screenshot excerpt from Ayush’s September 14 post describing Latham & Watkins building an internal AI stack with Nvidia servers.", caption: "Two sentences from Ayush’s commentary. The firm’s statements are in the linked Bloomberg Law report, hosted by Latham.", source: sources.openWeights },
    sources: [sources.latham, sources.openWeights],
  },
  whyNow: {
    title: "Why is the call coming now?",
    eyebrow: "The timing · Reports leading into the proposal",
    timeline: [
      { date: "August 26", title: "Swarm investigation", body: "METR reports on the OpenAI–Hugging Face incident.", source: sources.metr },
      { date: "September 9", title: "Lab disclosure", body: "Anthropic assesses four earlier evaluation incidents.", source: sources.assessment },
      { date: "September 11", title: "Enterprise alternative", body: "Reporting details Latham’s existing private AI infrastructure.", source: sources.latham },
      { date: "September 12", title: "Pacing proposal", body: "Amodei calls for embedded evaluators and regulated pacing.", source: sources.announcement },
    ],
    rationale: "Amodei cites accelerating AI-assisted AI development and the Hugging Face incident: independent scrutiny and shared limits would give safety work time to catch up.",
    boundary: "These are publication dates. Latham’s buildout began years earlier; no source establishes that it triggered the proposal. We will examine the competitive incentives separately.",
    sources: [sources.essay],
  },
  incentives: {
    title: "Who could benefit from new rules?",
    eyebrow: "The business hypothesis · An incentive, not a proven motive",
    chain: [
      { label: "Enterprise alternatives", body: "Private infrastructure and open weights give buyers another way to deploy AI." },
      { label: "Buyer leverage", body: "More options could reduce dependence on frontier APIs and pressure their pricing." },
      { label: "Potential entry barriers", body: "Costly certification or broad restrictions could be easier for large incumbents to absorb." },
    ],
    hypothesis: "Ayush argues regulation could protect incumbents. Competitive pressure is a plausible incentive to examine; it does not establish why Amodei or other leaders called for oversight.",
    boundary: "The September 14 critiques followed the proposal. Amodei does not cite Latham or propose a ban on enterprise open weights; specific rules and their effects still matter.",
    question: "Which requirements reduce demonstrated risks, and which mainly make competing harder?",
    sources: [sources.essay, sources.latham, sources.openWeights],
  },
  judgment: {
    title: "What should leaders demand from AI governance?",
    eyebrow: "Your decision lens · Safety and competition together",
    criteria: [
      { title: "Independent evidence", question: "Can reviewers inspect the system, publish findings, and prompt a response?" },
      { title: "Rules matched to risk", question: "Does each requirement address a demonstrated risk, with costs proportionate to the benefit?" },
      { title: "Meaningful model choice", question: "Can buyers still choose hosted or privately operated models that meet the same safety expectations?" },
    ],
    takeaway: "When a provider says its AI is safe, ask for the evidence. When it proposes a rule, ask how the rule changes both safety and competition.",
  },
};
